/**
 * Calculates the Probability Density Function (PDF) for a normal distribution.
 * @param x The value on the x-axis.
 * @param mean The mean (μ) of the distribution.
 * @param stdDev The standard deviation (σ) of the distribution.
 * @returns The probability density at point x.
 */
const normalPDF = (x: number, mean: number, stdDev: number) => {
  const M_SQRT_2_PI = Math.sqrt(2 * Math.PI)
  const exponent = -((x - mean) ** 2) / (2 * stdDev ** 2)
  return (1 / (stdDev * M_SQRT_2_PI)) * Math.exp(exponent)
}

/**
 * Generates an array of {x, y} points for the normal distribution curve.
 * @param mean The mean (μ) of the distribution.
 * @param stdDev The standard deviation (σ) of the distribution.
 * @param lbound - Lower boundary for data.
 * @param ubound - Upper boundary for data.
 * @param numPoints The number of points to generate for the curve.
 * @returns An array of data points.
 */
export const normalSample = (
  mean: number,
  stdDev: number,
  lbound?: number,
  ubound?: number,
  numPoints = 100,
): { x: number; y: number }[] => {
  const data = []
  // A good range is typically mean ± 3-4 standard deviations
  const range = 4 * stdDev
  const minX = lbound ?? mean - range
  const maxX = ubound ?? mean + range
  const step = (maxX - minX) / numPoints

  for (let i = 0; i <= numPoints; i++) {
    const x = minX + i * step
    const y = normalPDF(x, mean, stdDev)
    data.push({ x, y })
  }
  return data
}
